import styled from 'styled-components'
import { t, jt } from 'ttag'
import { useDispatch } from 'react-redux'
import { useMutation } from 'react-query'

import { SDB, SLB, WHITE, SLB90T, BLACK } from '@h2/components/colors'
import bannerSvg from '@h2/assets/svg/illustrations/upTheLadder/earlyLearnersBanner.svg'
import logo from '@h2/assets/svg/illustrations/upTheLadder/upTheLadderLogo.svg'
import { Flex } from '@h2/components/layout/flex'
import { Link } from '@h2/common/components/link'
import { museoFont } from '@h2/components/mixins'
import { Button } from '@h2/components/buttons/button'
import { api } from '@h2/api'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'

import { MuseoHeading } from '../common/museo'

const Wrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 20px;
  border-color: transparent;
  border-radius: 10px;
  background: radial-gradient(
    90% 70% at 15% 100%,
    ${WHITE},
    ${WHITE} 79.9%,
    ${SLB90T} 80%
  );
  min-height: 280px;
  flex-grow: 1;

  > :first-child {
    width: 100%;
  }
`
const ViewAllLink = styled(Link)`
  ${museoFont}
  font-weight: 900;
  margin-top: 5px;
  background-color: ${WHITE};
  color: ${SDB};
  z-index: 1;
  border-radius: 25px;

  :hover,
  :visited,
  :link {
    color: ${SDB};
    background-color: ${WHITE};
  }
`
const ContinueLink = styled(Link)`
  ${museoFont}
  font-weight: 500;
  z-index: 1;
  background-color: ${SLB};
  border-radius: 25px;

  :hover {
    background-color: ${WHITE};
    color: ${BLACK};
    outline: ${SLB} solid 3px;
    transition: outline 300ms;
  }
`
const StartButton = styled(Button)`
  ${museoFont}
  font-weight: 500;
  z-index: 1;
  border-radius: 25px;

  :hover {
    background-color: ${WHITE};
    color: ${BLACK};
    outline: ${SLB} solid 3px;
    transition: outline 300ms;
  }
`
const Banner = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`
const Logo = styled.img`
  margin-right: auto;
  margin-bottom: auto;
  width: 145px;
`
const gameSpan = <span key="game">{t`your game`}</span>
const LegacyContinue = ({ gameId }) => {
  const dispatch = useDispatch()
  const continueButtons = () => {
    return (
      <>
        <ContinueLink
          href={`/early/game/${gameId}/home`}
          ssr
          variant="button"
          padding="6px 50px"
          fontSize="20px"
          isHoverStyled={false}
          margin="0 0 10px 0">
          {t`Continue Game`}
        </ContinueLink>
        <ViewAllLink
          href="/ladder"
          variant="button"
          fontSize="16px"
          weight="bold"
          isHoverStyled={false}>
          {t`View All Games Here`}
        </ViewAllLink>
      </>
    )
  }
  const { mutate: newButtonSubmit, isLoading } = useMutation(
    () => {
      return api.fetch('/early/utl', {
        method: 'POST',
        body: JSON.stringify({})
      })
    },
    {
      onSuccess: newGame => {
        window.location.href = `/early/game/${newGame.id}/home`
      },
      onError: () => {
        dispatch(
          showUserFeedback(
            t`An error while creating a new game. Please try again later.`,
            'error'
          )
        )
      }
    }
  )
  const newButton = () => {
    return (
      <StartButton
        onClick={newButtonSubmit}
        variant="cta"
        padding="6px 50px"
        fontSize="20px"
        isLoading={isLoading}
        isHoverStyled={false}
        margin="0 0 10px 0">
        {t`Start New Game`}
      </StartButton>
    )
  }

  return (
    <Wrap>
      <Flex wrap="wrap" justifyContent="flex-end" gap="5px">
        <Logo alt="Up The Ladder Logo" src={logo} />

        <Flex direction="column" alignItems="flex-end">
          <MuseoHeading level="2" mb="10px">
            {gameId ? jt`Continue ${gameSpan}` : t`Start a New Game`}
          </MuseoHeading>
          <MuseoHeading level="4" color={SLB} align="right">
            {gameId
              ? t`Keep exploring and earning badges`
              : t`Begin exploring to learn about work`}
          </MuseoHeading>
          {gameId ? continueButtons() : newButton()}
        </Flex>
      </Flex>
      <Banner alt={t`Early Learners Banner`} src={bannerSvg} />
    </Wrap>
  )
}

export { LegacyContinue }
